/** @file Firestore data converters with Zod validation + Timestamp coercion. */
import { Timestamp } from "firebase/firestore";
import { playDocSchema, teamDocSchema, userDocSchema, shareDocSchema } from "./schemas.js";
import { migratePlayV1ToV2 } from "./migrate-v1-to-v2.js";

/**
 * @param {unknown} raw
 * @returns {Date}
 */
function toDate(raw) {
  if (raw instanceof Timestamp) {
    return raw.toDate();
  }
  if (raw instanceof Date) {
    return raw;
  }
  return new Date();
}

/**
 * @returns {import("firebase/firestore").FirestoreDataConverter<import("../types/schemas.js").PlayDoc>}
 */
export function playConverter() {
  return {
    toFirestore(play) {
      const parsed = playDocSchema.parse(play);
      const { id: _id, createdAt, updatedAt, ...rest } = parsed;
      return {
        ...rest,
        createdAt: Timestamp.fromDate(createdAt),
        updatedAt: Timestamp.fromDate(updatedAt),
      };
    },
    fromFirestore(snap) {
      const raw = /** @type {Record<string, unknown>} */ (snap.data());
      const migrated = migratePlayV1ToV2({
        ...raw,
        id: snap.id,
        createdAt: toDate(raw.createdAt),
        updatedAt: toDate(raw.updatedAt),
      });
      return playDocSchema.parse(migrated);
    },
  };
}

/**
 * @returns {import("firebase/firestore").FirestoreDataConverter<import("../types/schemas.js").TeamDoc>}
 */
export function teamConverter() {
  return {
    toFirestore(team) {
      const parsed = teamDocSchema.parse(team);
      const { id: _id, createdAt, updatedAt, ...rest } = parsed;
      return {
        ...rest,
        createdAt: Timestamp.fromDate(createdAt),
        updatedAt: Timestamp.fromDate(updatedAt),
      };
    },
    fromFirestore(snap) {
      const raw = /** @type {Record<string, unknown>} */ (snap.data());
      return teamDocSchema.parse({
        ...raw,
        id: snap.id,
        createdAt: toDate(raw.createdAt),
        updatedAt: toDate(raw.updatedAt),
      });
    },
  };
}

/**
 * @returns {import("firebase/firestore").FirestoreDataConverter<import("../types/schemas.js").ShareDoc>}
 */
export function shareConverter() {
  return {
    toFirestore(share) {
      const parsed = shareDocSchema.parse(share);
      const { shareId: _shareId, createdAt, ...rest } = parsed;
      return {
        ...rest,
        shareId: _shareId,
        createdAt: Timestamp.fromDate(createdAt),
      };
    },
    fromFirestore(snap) {
      const raw = /** @type {Record<string, unknown>} */ (snap.data());
      return shareDocSchema.parse({
        ...raw,
        shareId: snap.id,
        createdAt: toDate(raw.createdAt),
      });
    },
  };
}

/**
 * @returns {import("firebase/firestore").FirestoreDataConverter<import("../types/schemas.js").UserDoc>}
 */
export function userConverter() {
  return {
    toFirestore(user) {
      const parsed = userDocSchema.parse(user);
      const { uid: _uid, createdAt, ...rest } = parsed;
      return {
        ...rest,
        createdAt: Timestamp.fromDate(createdAt),
      };
    },
    fromFirestore(snap) {
      const raw = /** @type {Record<string, unknown>} */ (snap.data());
      return userDocSchema.parse({
        ...raw,
        uid: snap.id,
        createdAt: toDate(raw.createdAt),
      });
    },
  };
}
